/**
 * One `activity` block, drawn.
 *
 * The whole component is `ActivityBlock` plus the one piece of state the data model deliberately
 * does not carry: whether the reader has folded this card. That belongs here rather than on the
 * block, because a `TurnBlock` is DATA — it comes from a live stream or from a reload and neither
 * knows what the reader did with the card in front of them. Folding is a property of the view.
 *
 * Everything else is `activityRow.tsx`'s mapping and the composite's own reconciliation. The card
 * has no opinion about the mark, the tone, the chevron, or the body — the fourteen `Agent tool`
 * boards and the eight `Agent state` boards differ only in those, and all twenty-two come out of
 * here as the same three lines.
 */
import { useCallback, useState } from 'react';
import { ActivityBlock, type ActivityActions, type ChatTheme } from '~/design-system/components/v1_5/Chat';
import {
  isFoldable,
  toActivityBody,
  toActivityRow,
  type ActivityLead,
  type ActivityTurnBlock,
} from './activityRow';

export interface ActivityCardProps {
  block: ActivityTurnBlock;
  theme: ChatTheme;
  /**
   * Start folded. Uncontrolled after mount — the reader's toggle is theirs, and a card that
   * re-folded itself because a later stream tick changed `lifecycle` would fight them.
   *
   * Defaults to open: a card arrives because something is happening, and the body is the report.
   * Nothing auto-folds on completion; the design draws a folded step (`Read 2 files`) but never
   * draws the fold happening, and a card that collapsed under the reader mid-turn would take the
   * only account of what the agent did off the screen at the moment they went to read it.
   */
  defaultCollapsed?: boolean;
  /** Overrides which mark leads the header. See `ActivityRowOptions.lead`. */
  lead?: ActivityLead;
  /**
   * Complete → `Accept`/`Reject` stretched, Failed → `Try again`/`Skip it` inline.
   *
   * A pass-through, and empty in the pane today: nothing on the wire proposes work the user has to
   * resolve, and the handlers could not live on a `TurnBlock` anyway — a callback in a data model is
   * one that has to be rebuilt on every rehydrate. The prop is here because the eight `Agent state`
   * artboards are not complete without it, and because whoever wires the accept/reject flow should
   * not have to reach past this card to draw its own pills.
   */
  actions?: ActivityActions;
}

export function ActivityCard({
  block,
  theme,
  defaultCollapsed = false,
  lead,
  actions,
}: ActivityCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const toggle = useCallback(() => setCollapsed((was) => !was), []);

  // A row with nothing under it gets no handler at all. `ActivityBlock` would strip the chevron
  // anyway, but it cannot un-press a button, and a `<button aria-expanded>` over an empty subtree
  // is the failure BUILD-CONTRACT §6 names. Pills count as content, the same way the block counts
  // them — folding a Complete card has to take its Accept and Reject with it.
  const foldable = isFoldable(block.body) || Boolean(actions);

  return (
    <ActivityBlock
      theme={theme}
      row={toActivityRow({
        block,
        collapsed,
        foldable,
        lead,
        onToggle: foldable ? toggle : undefined,
        theme,
      })}
      actions={collapsed ? undefined : actions}
      /* Folded means the body is gone, not hidden — `ActivityRow`'s two tiers are "a row you can
         still act on" and "a row that has finished and folded away", and the second one has nothing
         beneath it by definition. The block's `aria-live` wrapper survives the swap either way,
         which is the whole reason DECISIONS.md §8 keeps it rendered over `kind: 'none'`. */
      body={collapsed ? { kind: 'none' } : toActivityBody(block.body)}
    />
  );
}
